import { useCallback, useMemo, useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  ExploreResourcesAnalyticsEvent,
  GroupClassSeriesAnalyticsEvent,
  RecommendedGroupClassModalAnalyticsEvent,
  RecommendedGroupClassModalAnalyticsSource,
} from "@/constants/analytics/Analytics";
import { GroupClassRegistrationSource } from "@/constants/GroupClass";
import { ModalPath } from "@/constants/Navigation";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import type { RecommendedGroupClassInstance } from "@/store/api/schedule/groupClasses";
import { useRegisterForGroupClassMutation } from "@/store/api/schedule/groupClasses";
import { formatTimeSlotLabel } from "@/ui/components/groupClasses/series/TimeSlotSelector";
import {
  clearRecommendedGroupClassStorage,
  DismissalMode,
  setRecommendedGroupClassNoneOfTheseTimes,
  setRecommendedGroupClassRemindMeLaterDate,
} from "@/utils/recommendedGroupClass/storage.util";

const DefaultErrorMessage = "Registration failed. Please try again.";

/**
 * Returns the effective dismissal mode for a recommended group class based on the dismissal mode and error message.
 * If there is an error and the dismissal mode is NoneOfTheseTimes, returns RemindMeLater to prevent permanently dismissing the modal.
 * Otherwise, returns the dismissal mode.
 */
const getEffectiveDismissalMode = ({
  dismissalMode,
  errorMessage,
}: {
  dismissalMode: DismissalMode;
  errorMessage: string | undefined;
}): DismissalMode =>
  !isNil(errorMessage) && dismissalMode === DismissalMode.NoneOfTheseTimes
    ? DismissalMode.RemindMeLater
    : dismissalMode;

type UseRecommendedGroupClassModalHandlersParams = {
  appUserId?: string;
  dismissalMode: DismissalMode;
  faqTitle: string;
  onDismissComplete: () => void;
  onRegisterSuccess: (groupClassExternalId: string) => void;
  patientExternalId?: string;
};

type RegisterPressArgs = {
  defaultInstanceId?: string;
  selectedInstance?: RecommendedGroupClassInstance;
};

const useRecommendedGroupClassModalHandlers = ({
  appUserId,
  dismissalMode,
  faqTitle,
  onDismissComplete,
  onRegisterSuccess,
  patientExternalId,
}: UseRecommendedGroupClassModalHandlersParams) => {
  const { track } = useAnalytics();
  const { captureMessage } = useAppMonitor();
  const [registerForGroupClass, { isLoading: isRegistering }] =
    useRegisterForGroupClassMutation();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  const effectiveDismissalMode = useMemo(
    () =>
      getEffectiveDismissalMode({
        dismissalMode,
        errorMessage,
      }),
    [dismissalMode, errorMessage],
  );

  const handleAccordionToggle = useCallback(
    (isOpen: boolean) => {
      track(ExploreResourcesAnalyticsEvent.InformationalBlurbRead, {
        Action: isOpen ? "Expand" : "Collapse",
        [AnalyticsFieldName.Component]: AnalyticsComponent.Accordion,
        [AnalyticsFieldName.Source]: RecommendedGroupClassModalAnalyticsSource,
        [AnalyticsFieldName.Title]: faqTitle,
      });
    },
    [faqTitle, track],
  );

  const handleRegisterPress = useCallback(
    async ({ defaultInstanceId, selectedInstance }: RegisterPressArgs) => {
      if (isRegistering) {
        return;
      }

      setErrorMessage(undefined);

      // It would be incredibly unlikely to reach this scenario, but it's technically possible based on the TS.
      // Handling it here to prevent bad API calls and to let us know.
      if (isNil(selectedInstance)) {
        captureMessage(
          "Cannot register for recommended group class: no selected instance found",
          {
            context: {
              defaultInstanceId,
            },
          },
        );
        setErrorMessage(DefaultErrorMessage);
        return;
      }

      const { endTime, groupClassExternalId, startTime } = selectedInstance;

      try {
        // Note: we do not need to pass the patientExternalId here
        // supports are registering for themselves, not their patients
        await registerForGroupClass({
          groupClassExternalId,
          source: GroupClassRegistrationSource.RecommendedGroupsModal,
        }).unwrap();

        clearRecommendedGroupClassStorage({
          appUserId,
          patientExternalId,
        });

        const selectedLabel = formatTimeSlotLabel({ endTime, startTime });
        const isTimeSlotDefault = groupClassExternalId === defaultInstanceId;

        track(GroupClassSeriesAnalyticsEvent.RegisterPressed, {
          [AnalyticsFieldName.Component]: ModalPath.RecommendedGroupClass,
          [AnalyticsFieldName.GroupClassId]: groupClassExternalId,
          [AnalyticsFieldName.PatientId]: patientExternalId,
          [AnalyticsFieldName.SelectedTimeSlot]: selectedLabel,
          [AnalyticsFieldName.Source]:
            RecommendedGroupClassModalAnalyticsSource,
          [AnalyticsFieldName.TimeSlotWasDefault]: isTimeSlotDefault,
        });

        onRegisterSuccess(groupClassExternalId);
      } catch {
        setErrorMessage(DefaultErrorMessage);
      }
    },
    [
      appUserId,
      captureMessage,
      isRegistering,
      onRegisterSuccess,
      patientExternalId,
      registerForGroupClass,
      track,
    ],
  );

  const handleRemindMeLaterPress = () => {
    setRecommendedGroupClassRemindMeLaterDate({
      appUserId,
      date: new Date(),
      patientExternalId,
    });
    track(RecommendedGroupClassModalAnalyticsEvent.RemindMeLaterPressed, {
      [AnalyticsFieldName.Component]: ModalPath.RecommendedGroupClass,
      [AnalyticsFieldName.Source]: RecommendedGroupClassModalAnalyticsSource,
    });
    onDismissComplete();
  };

  const handleNoneOfTheseTimesPress = () => {
    track(RecommendedGroupClassModalAnalyticsEvent.NoneOfTheseTimesPressed, {
      [AnalyticsFieldName.Component]: ModalPath.RecommendedGroupClass,
      [AnalyticsFieldName.Source]: RecommendedGroupClassModalAnalyticsSource,
    });
  };

  const handleDeclineSubmit = () => {
    setRecommendedGroupClassNoneOfTheseTimes({
      appUserId,
      isEnabled: true,
      patientExternalId,
    });
    onDismissComplete();
  };

  return {
    effectiveDismissalMode,
    errorMessage,
    handleAccordionToggle,
    handleDeclineSubmit,
    handleNoneOfTheseTimesPress,
    handleRegisterPress,
    handleRemindMeLaterPress,
    isRegistering,
  };
};

export { useRecommendedGroupClassModalHandlers };
